(() => {
    const STORES_URL =
        'https://fd.chemistwarehouse.co.nz/primarycontainer/ubStoreDetails.json';

    async function fetchStores() {
        const response = await fetch(STORES_URL);

        if (!response.ok) {
            throw new Error(
                `Failed to fetch stores: ${response.status} ${response.statusText}`
            );
        }

        const data = await response.json();

        if (!Array.isArray(data)) {
            throw new Error('Unexpected store data format.');
        }

        return data
            .flatMap(group =>
                Array.isArray(group.groupingData) ? group.groupingData : []
            )
            .map(normaliseStore);
    }

    function normaliseStore(store) {
        const cities = Array.isArray(store.tags)
            ? store.tags.map(tag => tag?.Value).filter(Boolean)
            : [];

        return {
            name: store.title || '',
            subtitle: store.subtitle || '',
            city: cities[0] || '',
            cities,
            address: store.address || '',
            phone: store.phone || '',
            latitude: store.geo?.Latitude ?? null,
            longitude: store.geo?.Longitude ?? null,

            hours: Object.fromEntries(
                (store.openHours || []).map(
                    ({ WeekDay, OpenTime, CloseTime }) => [
                        WeekDay,
                        `${formatTime(OpenTime)} – ${formatTime(CloseTime)}`
                    ]
                )
            )
        };
    }

    function formatTime(time) {
        if (!time) return '';

        const [hour, minute] = time.split(':').map(Number);

        if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
            return time;
        }

        return new Intl.DateTimeFormat('en-NZ', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        }).format(new Date(2000, 0, 1, hour, minute));
    }

    function normaliseCity(value) {
        return (value || '').trim().toLowerCase();
    }

    function formatPhoneHref(phone) {
        return `tel:${phone.replace(/[^\d+]/g, '')}`;
    }

    function getCityFromUrl() {
        const params = new URLSearchParams(window.location.search);
        return params.get('city');
    }

    function getStoresForCity(stores, city) {
        if (!city) return [];

        const target = normaliseCity(city);

        return stores.filter(store =>
            (store.cities || [store.city]).some(
                storeCity => normaliseCity(storeCity) === target
            )
        );
    }

    function getMapsUrl(store) {
        if (store.latitude != null && store.longitude != null) {
            return `https://www.google.com/maps/search/?api=1&query=${store.latitude},${store.longitude}`;
        }

        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
            store.address
        )}`;
    }

    function renderHours(hours) {
        return Object.entries(hours)
            .map(
                ([day, time]) => `
          <div class="found-it__hours-row">
            <span class="found-it__hours-day">${day}</span>
            <span class="found-it__hours-time">${time}</span>
          </div>
        `
            )
            .join('');
    }

    function renderStore(store, index) {
        const detailsId = `found-it-store-details-${index}`;

        return `
      <article class="found-it__store">
        <div class="found-it__store-main">
          <div class="found-it__store-copy">
            <h2 class="found-it__store-name">${store.name}</h2>
            <p class="found-it__store-city">${store.city}</p>
            <p class="found-it__store-address">${store.address}</p>
          </div>

        </div>

        <div class="found-it__store-actions">
            <a
              class="found-it__action-link"
              href="${getMapsUrl(store)}"
              target="_blank"
              rel="noopener noreferrer"
            >
              Directions
            </a>

            <a
              class="found-it__action-link"
              href="${formatPhoneHref(store.phone)}"
            >
              CALL ${store.phone}
            </a>

            <button
                class="found-it__details-toggle"
                type="button"
                aria-expanded="false"
                aria-controls="${detailsId}"
                >
                <span>More details</span>
                <span class="found-it__details-icon" aria-hidden="true">+</span>
            </button>
          </div>

        <div
          class="found-it__details"
          id="${detailsId}"
          hidden
        >
          <h3 class="found-it__hours-heading">Opening hours</h3>

          <div class="found-it__hours">
            ${renderHours(store.hours)}
          </div>
        </div>

      </article>
    `;
    }

    function renderStores(stores, city) {
        const results = document.querySelector('[data-found-it-results]');
        const heading = document.querySelector('[data-found-it-title]');
        const empty = document.querySelector('[data-found-it-empty]');
        const capitalisedCity = city === "" ? "" : city.charAt(0).toUpperCase() + city.slice(1);

        if (!results) return;

        if (heading) {
            heading.textContent = city
                ? `Stores in ${capitalisedCity}`
                : 'Find a store';
        }

        if (!city) {
            results.innerHTML = '';

            if (empty) {
                empty.hidden = false;
                empty.textContent = 'Please choose a city.';
            }

            return;
        }

        if (!stores.length) {
            results.innerHTML = '';

            if (empty) {
                empty.hidden = false;
                empty.textContent = `We couldn't find any stores in ${city}.`;
            }

            return;
        }

        if (empty) {
            empty.hidden = true;
        }

        results.innerHTML = stores.map(renderStore).join('');
    }

    function setupDetailsToggles() {
        document.addEventListener('click', event => {
            const button = event.target.closest('.found-it__details-toggle');

            if (!button) return;

            const detailsId = button.getAttribute('aria-controls');
            const details = document.getElementById(detailsId);

            if (!details) return;

            const isExpanded =
                button.getAttribute('aria-expanded') === 'true';

            button.setAttribute(
                'aria-expanded',
                String(!isExpanded)
            );

            details.hidden = isExpanded;

            const text = button.querySelector('span:first-child');
            const icon = button.querySelector(
                '.found-it__details-icon'
            );

            if (text) {
                text.textContent = isExpanded
                    ? 'More details'
                    : 'Less details';
            }

            if (icon) {
                icon.textContent = isExpanded ? '+' : '−';
            }
        });
    }

    function setupCarousel() {
        const carousel = document.querySelector(
            '[data-found-it-carousel]'
        );

        if (!carousel) return;

        const track = carousel.querySelector(
            '[data-carousel-track]'
        );

        const slides = Array.from(
            carousel.querySelectorAll('[data-carousel-slide]')
        );

        const prevButton = carousel.querySelector(
            '[data-carousel-prev]'
        );

        const nextButton = carousel.querySelector(
            '[data-carousel-next]'
        );

        const dots = Array.from(
            carousel.querySelectorAll('[data-carousel-dot]')
        );

        if (!track || !slides.length) return;

        let currentIndex = 0;

        function updateCarousel() {
            track.style.transform =
                `translateX(-${currentIndex * 100}%)`;

            slides.forEach((slide, index) => {
                slide.setAttribute(
                    'aria-hidden',
                    String(index !== currentIndex)
                );
            });

            dots.forEach((dot, index) => {
                const active = index === currentIndex;

                dot.classList.toggle('is-active', active);

                dot.setAttribute(
                    'aria-current',
                    active ? 'true' : 'false'
                );
            });
        }

        function goTo(index) {
            if (index < 0) {
                currentIndex = slides.length - 1;
            } else if (index >= slides.length) {
                currentIndex = 0;
            } else {
                currentIndex = index;
            }

            updateCarousel();
        }

        if (prevButton) {
            prevButton.addEventListener('click', () => {
                goTo(currentIndex - 1);
            });
        }

        if (nextButton) {
            nextButton.addEventListener('click', () => {
                goTo(currentIndex + 1);
            });
        }

        dots.forEach((dot, index) => {
            dot.addEventListener('click', () => {
                goTo(index);
            });
        });

        updateCarousel();
    }

    async function init() {
        const city = getCityFromUrl();
        const empty = document.querySelector(
            '[data-found-it-empty]'
        );

        try {
            const stores = await fetchStores();
            const cityStores = getStoresForCity(stores, city);

            renderStores(cityStores, city);
        } catch (error) {
            console.error(
                '[found-it] Failed to load stores:',
                error
            );

            const results = document.querySelector(
                '[data-found-it-results]'
            );

            if (results) {
                results.innerHTML = '';
            }

            if (empty) {
                empty.hidden = false;
                empty.textContent =
                    'Sorry, we couldn’t load the store information. Please try again later.';
            }
        }

        setupDetailsToggles();
        setupCarousel();
    }

    if (document.readyState === 'loading') {
        document.addEventListener(
            'DOMContentLoaded',
            init
        );
    } else {
        init();
    }
})();